import React, { useEffect, useState, useMemo } from "react";
import {
  Table,
  Tag,
  Card,
  message,
  Button,
  Input,
  Space,
  Modal,
  Tabs,
  Descriptions,
  Divider,
} from "antd";
import {
  SearchOutlined,
  DownloadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import axiosInstance from "../../../configs";
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/id";

dayjs.locale("id");

const { TabPane } = Tabs;

const IndexBrowse = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Detail Modal State
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState({
    header: {},
    barang: [],
    entitas: [],
    kemasan: [],
    kontainer: [],
    dokumen: [],
    pengangkut: [],
  });

  const searchInput = React.useRef(null);
  const history = useHistory();

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex, placeholder) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={placeholder || `Cari ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
          >
            Cari
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small">
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
  });

  const columns = [
    {
      title: "No",
      key: "nomor_urut",
      width: 60,
      align: "center",
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "Kode Dokumen",
      dataIndex: "kode_dokumen",
      key: "kode_dokumen",
      width: 150,
      ...getColumnSearchProps("kode_dokumen", "Cari Kode"),
    },
    {
      title: "Nomor Pengajuan",
      dataIndex: "nomor_aju",
      key: "nomor_aju",
      width: 200,
      render: (text) => (
        <b>{text?.replace(/(\d{6})(\d{6})(\d{8})(\d{6})/, "$1-$2-$3-$4")}</b>
      ),
      ...getColumnSearchProps("nomor_aju", "Cari No Aju"),
    },
    {
      title: "Nomor Pendaftaran",
      dataIndex: "nomor_daftar",
      key: "nomor_daftar",
      width: 150,
      ...getColumnSearchProps("nomor_daftar", "Cari No Daftar"),
    },
    {
      title: "Tanggal Pendaftaran",
      dataIndex: "tanggal_daftar",
      key: "tanggal_daftar",
      width: 150,
      render: (text) => (text ? dayjs(text).format("DD MMMM YYYY") : "-"),
    },
    {
      title: "Pemilik Barang",
      dataIndex: "nama_entitas",
      key: "nama_entitas",
      width: 300,
      ...getColumnSearchProps("nama_entitas", "Cari Pemilik Barang"),
    },
    {
      title: "Kode Respon",
      dataIndex: "nama_respon",
      key: "nama_respon",
      width: 150,
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Aksi",
      key: "aksi",
      fixed: "right",
      width: 250,
      render: (_, record) => {
        // Check if draft status: kode_respon ends with 099 or 09
        const kodeRespon = record.kode_respon?.toString() || "";
        const isDraft = kodeRespon.endsWith("099") || kodeRespon.endsWith("09");

        return (
          <Space>
            <Button
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
              type="default"
              size="small"
            >
              Lihat Detail
            </Button>
            {isDraft ? (
              <Button
                icon={<DownloadOutlined />}
                onClick={() => handleDownloadDraft(record)}
                size="small"
                style={{
                  backgroundColor: "#17a2b8",
                  color: "#fff",
                  borderColor: "#17a2b8",
                }}
              >
                Draft Excel
              </Button>
            ) : (
              <Button
                icon={<DownloadOutlined />}
                onClick={() => handleDownloadPdf(record)}
                size="small"
              >
                PDF
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  const handleDownloadDraft = async (record) => {
    const nomorAju = record.nomor_aju;
    if (!nomorAju) {
      message.error("Nomor AJU tidak ditemukan.");
      return;
    }

    try {
      message.loading({ content: "Mengunduk draft...", key: "downloadDraft" });
      const response = await axiosInstance.get(
        `/portal/pasifik/download-draft/${nomorAju}`,
        {
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `DRAFT_${nomorAju}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success({
        content: "Draft berhasil didownload!",
        key: "downloadDraft",
      });
    } catch (error) {
      console.error("Download draft error:", error);
      message.error({
        content: "Gagal mengunduh draft.",
        key: "downloadDraft",
      });
    }
  };

  const handleDownloadPdf = async (record) => {
    // Implement PDF download logic here using proxy
    message.info("Fitur download PDF belum diimplementasikan sepenuhnya.");
  };

  const handleViewDetail = async (record) => {
    setSelectedRecord(record);
    setDetailVisible(true);
    setDetailLoading(true);

    const nomorDaftar = record.nomor_daftar;
    if (!nomorDaftar) {
      message.error("Nomor Pendaftaran tidak ditemukan.");
      setDetailLoading(false);
      return;
    }

    try {
      const [
        headerRes,
        barangRes,
        entitasRes,
        kemasanRes,
        kontainerRes,
        dokumenRes,
        pengangkutRes,
      ] = await Promise.all([
        axiosInstance.get(`/portal/pasifik/header/${nomorDaftar}`),
        axiosInstance.get(
          `/portal/pasifik/get-data-barang?nomorPendaftaran=${nomorDaftar}&kodeDokumen=${record.kode_dokumen}&tanggalDaftar=${record.tanggal_daftar}`,
        ),
        axiosInstance.get(
          `/portal/pasifik/get-data-entitas?nomorPendaftaran=${nomorDaftar}`,
        ),
        axiosInstance.get(
          `/portal/pasifik/get-data-kemasan?nomorPendaftaran=${nomorDaftar}`,
        ),
        axiosInstance.get(
          `/portal/pasifik/get-data-kontainer?nomorPendaftaran=${nomorDaftar}`,
        ),
        axiosInstance.get(
          `/portal/pasifik/get-data-dokumen?nomorPendaftaran=${nomorDaftar}`,
        ),
        axiosInstance.get(
          `/portal/pasifik/get-data-pengangkut?nomorPendaftaran=${nomorDaftar}`,
        ),
      ]);

      setDetailData({
        header: headerRes.data || {},
        barang: Array.isArray(barangRes.data.data)
          ? barangRes.data.data
          : Array.isArray(barangRes.data)
            ? barangRes.data
            : [],
        entitas: Array.isArray(entitasRes.data) ? entitasRes.data : [],
        kemasan: Array.isArray(kemasanRes.data) ? kemasanRes.data : [],
        kontainer: Array.isArray(kontainerRes.data) ? kontainerRes.data : [],
        dokumen: Array.isArray(dokumenRes.data) ? dokumenRes.data : [],
        pengangkut: Array.isArray(pengangkutRes.data) ? pengangkutRes.data : [],
      });
    } catch (error) {
      console.error("Error fetching details:", error);
      message.error("Gagal mengambil detail dokumen.");
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        "/portal/pasifik/browse-data-header",
      );
      if (Array.isArray(response.data)) {
        setData(response.data);
      } else if (response.data && Array.isArray(response.data.data)) {
        setData(response.data.data);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      message.error("Gagal mengambil data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- Columns for Detail Tables ---
  const barangColumns = [
    { title: "Seri", dataIndex: "seri_barang", key: "seri_barang", width: 70 },
    { title: "HS Code", dataIndex: "kode_hs", key: "kode_hs", width: 120 },
    { title: "Uraian", dataIndex: "uraian", key: "uraian" },
    {
      title: "Jumlah",
      dataIndex: "jumlah_satuan",
      key: "jumlah_satuan",
      width: 100,
    },
    {
      title: "Satuan",
      dataIndex: "kode_satuan",
      key: "kode_satuan",
      width: 100,
    },
    {
      title: "CIF",
      dataIndex: "cif",
      key: "cif",
      width: 150,
      render: (val) =>
        val ? `Rp ${Number(val).toLocaleString("id-ID")}` : "-",
    },
  ];

  const entitasColumns = [
    {
      title: "Seri",
      dataIndex: "seri",
      key: "seri",
      width: 70,
    },
    {
      title: "Jenis",
      dataIndex: "kode_entitas",
      key: "kode_entitas",
      width: 100,
      render: (val) =>
        val === "3" ? "Importir" : val === "1" ? "Pemilik" : val,
    },
    {
      title: "Identitas",
      dataIndex: "nomor_identitas",
      key: "nomor_identitas",
      width: 150,
    },
    { title: "Nama", dataIndex: "nama_entitas", key: "nama_entitas" },
    { title: "Alamat", dataIndex: "alamat_entitas", key: "alamat_entitas" },
  ];

  const kemasanColumns = [
    {
      title: "Seri",
      dataIndex: "seri",
      key: "seri",
      width: 70,
    },
    {
      title: "Jenis",
      dataIndex: "kode_kemasan",
      key: "kode_kemasan",
    },
    { title: "Jumlah", dataIndex: "jumlah_kemasan", key: "jumlah_kemasan" },
    { title: "Merek", dataIndex: "merk", key: "merk" },
  ];

  const kontainerColumns = [
    { title: "Seri", dataIndex: "seri", key: "seri", width: 70 },
    { title: "Nomor", dataIndex: "nomor_kontiner", key: "nomor_kontiner" },
    {
      title: "Ukuran",
      dataIndex: "kode_ukuran_kontainer",
      key: "kode_ukuran_kontainer",
    },
    {
      title: "Tipe",
      dataIndex: "kode_tipe_kontainer",
      key: "kode_tipe_kontainer",
    },
  ];

  const dokumenColumns = [
    {
      title: "Seri",
      dataIndex: "seri",
      key: "seri",
      width: 70,
    },
    { title: "Kode", dataIndex: "kode_dokumen", key: "kode_dokumen" },
    { title: "Nomor", dataIndex: "nomor_dokumen", key: "nomor_dokumen" },
    {
      title: "Tanggal",
      dataIndex: "tanggal_dokumen",
      key: "tanggal_dokumen",
      render: (val) => (val ? dayjs(val).format("DD MMM YYYY") : "-"),
    },
  ];

  return (
    <div style={{ marginTop: 10 }}>
      {/* Search & Filter UI could go here, but currently integrated in columns */}

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        scroll={{ x: "max-content" }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          defaultPageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} dari ${total} dokumen`,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
        size="middle"
      />

      <Modal
        title={
          <Space>
            <EyeOutlined /> Detail Dokumen Pabean
          </Space>
        }
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailVisible(false)}>
            Tutup
          </Button>,
        ]}
        width="95%"
        style={{ top: 10, maxWidth: 1000, padding: 0 }}
        bodyStyle={{ padding: 0, height: "80vh", overflowY: "hidden" }} // Full height modal for mobile
      >
        {detailLoading ? (
          <div style={{ padding: 24, textAlign: "center" }}>
            Loading details...
          </div>
        ) : (
          <Tabs
            defaultActiveKey="header"
            tabPosition="top"
            style={{ height: "100%" }}
          >
            {" "}
            {/* Changed to top for mobile */}
            <TabPane tab="Header" key="header">
              <div
                style={{
                  padding: 12,
                  overflowY: "auto",
                  maxHeight: "calc(80vh - 60px)",
                }}
              >
                {" "}
                {/* Scrollable content */}
                <Descriptions
                  title="Informasi Utama"
                  bordered
                  column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
                  size="small"
                  layout="vertical"
                >
                  {" "}
                  {/* Responsive columns */}
                  <Descriptions.Item label="Nomor Pengajuan">
                    {detailData.header.nomor_aju}
                  </Descriptions.Item>
                  <Descriptions.Item label="Nomor Pendaftaran">
                    {detailData.header.nomor_daftar}
                  </Descriptions.Item>
                  <Descriptions.Item label="Tanggal Pendaftaran">
                    {detailData.header.tanggal_daftar
                      ? dayjs(detailData.header.tanggal_daftar).format(
                          "DD MMMM YYYY",
                        )
                      : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Kode Dokumen">
                    {detailData.header.kode_dokumen}
                  </Descriptions.Item>
                  <Descriptions.Item label="Kantor Pabean">
                    {detailData.header.kode_kantor}
                  </Descriptions.Item>
                </Descriptions>
                <Divider style={{ margin: "12px 0" }} />
                <Descriptions
                  title="Keuangan"
                  bordered
                  column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
                  size="small"
                  layout="vertical"
                >
                  <Descriptions.Item label="Nilai CIF">
                    Rp{" "}
                    {Number(detailData.header.cif || 0).toLocaleString("id-ID")}
                  </Descriptions.Item>
                  <Descriptions.Item label="Bruto">
                    {detailData.header.bruto} Kg
                  </Descriptions.Item>
                  <Descriptions.Item label="Netto">
                    {detailData.header.netto} Kg
                  </Descriptions.Item>
                </Descriptions>
              </div>
            </TabPane>
            <TabPane tab="Entitas" key="entitas">
              <div style={{ padding: 12, overflowX: "auto" }}>
                <Table
                  dataSource={detailData.entitas}
                  columns={entitasColumns}
                  rowKey="seri_entitas"
                  pagination={false}
                  size="small"
                  scroll={{ x: true }}
                />
              </div>
            </TabPane>
            <TabPane tab="Barang" key="barang">
              <div style={{ padding: 12, overflowX: "auto" }}>
                <Table
                  dataSource={detailData.barang}
                  columns={barangColumns}
                  rowKey="seri_barang"
                  pagination={{ pageSize: 5 }}
                  size="small"
                  scroll={{ x: true }}
                />
              </div>
            </TabPane>
            <TabPane tab="Kemasan" key="kemasan">
              <div style={{ padding: 12, overflowX: "auto" }}>
                <Table
                  dataSource={detailData.kemasan}
                  columns={kemasanColumns}
                  rowKey="seri_kemasan"
                  pagination={false}
                  size="small"
                  scroll={{ x: true }}
                />
              </div>
            </TabPane>
            <TabPane tab="Kontainer" key="kontainer">
              <div style={{ padding: 12, overflowX: "auto" }}>
                <Table
                  dataSource={detailData.kontainer}
                  columns={kontainerColumns}
                  rowKey="seri_kontainer"
                  pagination={false}
                  size="small"
                  scroll={{ x: true }}
                />
              </div>
            </TabPane>
            <TabPane tab="Dokumen" key="dokumen">
              <div style={{ padding: 12, overflowX: "auto" }}>
                <Table
                  dataSource={detailData.dokumen}
                  columns={dokumenColumns}
                  rowKey="seri_dokumen"
                  pagination={false}
                  size="small"
                  scroll={{ x: true }}
                />
              </div>
            </TabPane>
            <TabPane tab="Pengangkut" key="pengangkut">
              <div
                style={{
                  padding: 12,
                  overflowY: "auto",
                  maxHeight: "calc(80vh - 60px)",
                }}
              >
                {detailData.pengangkut.map((p, idx) => (
                  <Descriptions
                    key={idx}
                    title={`Pengangkut #${idx + 1}`}
                    bordered
                    column={1}
                    size="small"
                    style={{ marginBottom: 16 }}
                  >
                    <Descriptions.Item label="Nama Pengangkut">
                      {p.nama_pengangkut}
                    </Descriptions.Item>
                    <Descriptions.Item label="Nomor Pengangkut">
                      {p.nomor_pengangkut}
                    </Descriptions.Item>
                    <Descriptions.Item label="Cara Angkut">
                      {p.kode_cara_angkut}
                    </Descriptions.Item>
                  </Descriptions>
                ))}
                {detailData.pengangkut.length === 0 && (
                  <div style={{ textAlign: "center", color: "#999" }}>
                    Tidak ada data pengangkut
                  </div>
                )}
              </div>
            </TabPane>
          </Tabs>
        )}
      </Modal>
    </div>
  );
};

export default IndexBrowse;
